import { fetchAllActiveAssemblies } from '@/api/assemblies.api';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { AssemblyModel } from '@/types/assembly.types';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

/** Ascending by NC number; the rare row without one sorts last. */
function compareByNcNumber(a: AssemblyModel, b: AssemblyModel): number {
  if (a.nc_number == null) return b.nc_number == null ? 0 : 1;
  if (b.nc_number == null) return -1;
  return a.nc_number - b.nc_number;
}

function groupByToolId(assemblies: AssemblyModel[]): Map<string, AssemblyModel[]> {
  const byToolId = new Map<string, AssemblyModel[]>();
  for (const assembly of assemblies) {
    const bucket = byToolId.get(assembly.tool_id);
    if (bucket) bucket.push(assembly);
    else byToolId.set(assembly.tool_id, [assembly]);
  }
  for (const bucket of byToolId.values()) bucket.sort(compareByNcNumber);
  return byToolId;
}

/**
 * Every active assembly, indexed by the tool it is mounted on.
 *
 * The tools table reads a tool's holder and NC number out of this map, because since the
 * tool-database redesign neither lives on the tool itself — one tool can sit in several
 * assemblies, each with its own holder and magazine slot.
 *
 * Loading is independent of the tool list on purpose: the table renders its rows as soon
 * as the tools arrive and fills these two columns once the assemblies follow. A failed
 * refetch keeps the previously loaded map rather than blanking the columns.
 */
export const useToolAssemblies = () => {
  const { toolSystem } = useToolSystem();
  const [byToolId, setByToolId] = useState<Map<string, AssemblyModel[]>>(() => new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssemblies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const assemblies = await fetchAllActiveAssemblies(toolSystem);
      setByToolId(groupByToolId(assemblies));
    } catch (_err) {
      const message = 'Failed to load tool assemblies';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [toolSystem]);

  useEffect(() => {
    fetchAssemblies();
  }, [fetchAssemblies]);

  return {
    byToolId,
    isLoading,
    error,
    refetch: fetchAssemblies,
  };
};
